import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth, storage } from "../firebase";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import ImageUpload from "./ImageUpload";
import "./ProductManager.css";

export default function ProductManager() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    image: "",
    availability: "In stock"
  });

  // Fetch products from Firestore
  const fetchProducts = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "products"));
      const productsData = [];
      querySnapshot.forEach((doc) => {
        productsData.push({ id: doc.id, ...doc.data() });
      });
      setProducts(productsData);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  // Filter and search products
  const filterProducts = () => {
    let filtered = products;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.price.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  };

  // Update filtered products when products or searchTerm changes
  useEffect(() => {
    filterProducts();
  }, [products, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Add new product
  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.price || !formData.image) {
      alert("Please fill in all required fields and upload an image");
      return;
    }
    
    try {
      // Check authentication
      if (!auth.currentUser) {
        alert("You must be logged in to add products. Please log in first.");
        return;
      }
      
      await addDoc(collection(db, "products"), {
        name: formData.name,
        price: formData.price,
        image: formData.image,
        availability: formData.availability,
        createdAt: serverTimestamp()
      });
      
      setFormData({ name: "", price: "", image: "", availability: "In stock" });
      setShowForm(false);
      fetchProducts();
      alert("Product added successfully!");
    } catch (error) {
      console.error("Error adding product:", error);
      if (error.code === 'permission-denied') {
        alert("Permission denied. Make sure you're logged in with the admin email.");
      } else {
        alert(`Error adding product: ${error.message}`);
      }
    }
  };

  // Update existing product
  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.price || !formData.image) {
      alert("Please fill in all required fields and ensure an image is uploaded");
      return;
    }
    
    try {
      const updateData = {
        name: formData.name,
        price: formData.price,
        image: formData.image,
        availability: formData.availability,
        updatedAt: serverTimestamp()
      };
      
      const productRef = doc(db, "products", editingProduct.id);
      await updateDoc(productRef, updateData);
      
      setFormData({ name: "", price: "", image: "", availability: "In stock" });
      setEditingProduct(null);
      setShowForm(false);
      fetchProducts();
      alert("Product updated successfully!");
    } catch (error) {
      console.error("Error updating product:", error);
      alert(`Error updating product: ${error.message}`);
    }
  };

  // Delete product
  const handleDeleteProduct = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        // First, get the product data to extract the image URL
        const productToDelete = products.find(p => p.id === productId);
        
        if (productToDelete && productToDelete.image) {
          try {
            // Extract the image path from the URL to delete from Storage
            // Firebase Storage URLs have format: https://firebasestorage.googleapis.com/v0/b/[bucket]/o/[path]?...
            const imageUrl = productToDelete.image;
            
            // Extract the file path from the URL
            if (imageUrl.includes('firebasestorage.googleapis.com')) {
              const urlParts = imageUrl.split('/o/')[1];
              if (urlParts) {
                const imagePath = urlParts.split('?')[0];
                const decodedPath = decodeURIComponent(imagePath);
                
                // Delete the image from Firebase Storage
                const imageRef = ref(storage, decodedPath);
                await deleteObject(imageRef);
                console.log("Image deleted from storage:", decodedPath);
              }
            }
          } catch (storageError) {
            console.error("Error deleting image from storage:", storageError);
            // Continue with product deletion even if image deletion fails
          }
        }
        
        // Delete the product document from Firestore
        await deleteDoc(doc(db, "products", productId));
        fetchProducts();
        alert("Product and image deleted successfully!");
      } catch (error) {
        console.error("Error deleting product:", error);
        alert("Error deleting product");
      }
    }
  };

  // Navigate to edit page
  const navigateToEdit = (product) => {
    navigate(`/admin/edit/${product.id}`);
  };

  // Navigate to add product page
  const navigateToAdd = () => {
    navigate('/admin/add');
  };

  // Quick stock toggle function
  const toggleStock = async (productId, currentStatus) => {
    try {
      const normalizedCurrentStatus = (currentStatus === 'In stock' || !currentStatus) ? 'In stock' : 'Sold out';
      const newStatus = normalizedCurrentStatus === 'In stock' ? 'Sold out' : 'In stock';
      const productRef = doc(db, "products", productId);
      await updateDoc(productRef, {
        availability: newStatus,
        updatedAt: serverTimestamp()
      });
      fetchProducts();
      alert(`Product status updated to: ${newStatus}`);
    } catch (error) {
      console.error("Error updating stock:", error);
      alert("Error updating stock status");
    }
  };

  // Cancel form
  const cancelForm = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormData({ name: "", price: "", image: "", availability: "In stock" });
  };

  return (
    <div className="product-manager">
      {/* Header Section */}
 

      {/* Search Section */}
      <div className="search-filter-section">
        <div className="search-controls">
          <div className="search-input-group">
            <input
              type="text"
              placeholder="Search products by name or price..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <img src="/images/icons8-search-50.png" alt="Search" className="search-icon" />
          </div>
          
          <div className="results-count">
            Found {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-buttons">
        <button 
          onClick={navigateToAdd}
          className="btn-primary"
        >
          Add New Product
        </button>
      </div>

      {/* Product Form */}
      {showForm && (
        <div className="product-form">
          <h3 className="form-title">{editingProduct ? "Edit Product" : "Add New Product"}</h3>
          <form onSubmit={editingProduct ? handleUpdateProduct : handleAddProduct}>
            <div className="form-group">
              <label className="form-label">Product Name:</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="form-input"
                placeholder="Enter product name"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Price:</label>
              <input
                type="text"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                required
                className="form-input"
                placeholder="e.g., $29.99"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Stock Status:</label>
              <select
                name="availability"
                value={formData.availability}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="In stock">In Stock</option>
                <option value="Sold out">Sold Out</option>
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">Product Image:</label>
              <ImageUpload 
                onImageUploaded={(imageUrl) => setFormData({...formData, image: imageUrl})}
                currentImage={formData.image}
              />
            </div>
            
            <div className="form-actions">
              <button type="submit" className="btn-success">
                {editingProduct ? "Update Product" : "Add Product"}
              </button>
              <button type="button" onClick={cancelForm} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Products Grid */}
      <div className="products-grid">
        {filteredProducts.map((product) => (
          <div key={product.id} className="product-card">
            <div className="product-image-container">
              <img 
                src={product.image} 
                alt={product.name}
                className="product-image"
              />
            </div>
            <div className="product-details">
              <h4 className="product-name">{product.name}</h4>
              <p className="product-price">{product.price}</p>
              <div className="stock-status">
                <span className="stock-label">Stock Status:</span>
                <label className="ios-toggle" title="Toggle stock status">
                  <input 
                    type="checkbox" 
                    checked={(product.availability === 'In stock' || !product.availability)}
                    onChange={() => toggleStock(product.id, product.availability || 'In stock')}
                  />
                  <span className="ios-slider"></span>
                </label>
                <span className={`status-text ${(product.availability === 'In stock' || !product.availability) ? 'text-available' : 'text-sold-out'}`}>
                  {(product.availability === 'In stock' || !product.availability) ? 'Available' : 'Sold Out'}
                </span>
              </div>
            </div>
            <div className="product-actions">
              <button 
                onClick={() => navigateToEdit(product)}
                className="btn-edit"
              >
                Edit
              </button>
              <button 
                onClick={() => handleDeleteProduct(product.id)}
                className="btn-danger"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredProducts.length === 0 && products.length > 0 && (
        <div style={{ textAlign: "center", padding: 60, color: "#6c757d", background: "white", borderRadius: "16px", margin: "20px 0" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔍</div>
          <h3 style={{ marginBottom: "8px", color: "#495057" }}>No products found</h3>
          <p style={{ margin: 0 }}>Try adjusting your search terms or filters</p>
        </div>
      )}

      {products.length === 0 && (
        <div style={{ textAlign: "center", padding: 60, color: "#6c757d", background: "white", borderRadius: "16px", margin: "20px 0" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>📦</div>
          <h3 style={{ marginBottom: "8px", color: "#495057" }}>No products yet</h3>
          <p style={{ margin: 0 }}>Add your first product to get started!</p>
        </div>
      )}
    </div>
  );
}
